import React, { useEffect, useState } from "react";
import Icon from "../shared/Icon";
import { connect } from "react-redux";
import { compose } from "redux";
import { withTranslation } from "react-i18next";
import cookies from "js-cookie";
import { fetchOrderDetail, isAbortError, splitVat, VAT_RATE } from "../../../services/managerApi";
import StatusPill from "../shared/StatusPill";
import { ErrorState, LoadingCard } from "../shared/States";
import {
  formatCurrency,
  formatDayTime,
  formatKilos,
  formatLongDate,
  formatShortDate,
} from "../shared/format";
import { buildTimeline } from "./timeline";

/** Le nom produit est localisé côté API via trois champs distincts. */
const productName = (product) => {
  const locale = cookies.get("i18next");
  if (locale === "du") return product?.name_nl || product?.name;
  if (locale === "fn") return product?.name_fr || product?.name;
  return product?.name;
};

const OrderDrawer = ({ t, authUser, order, onClose, onPrev, onNext, onDownloadReceipt }) => {
  const [detail, setDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    (async () => {
      setIsLoading(true);
      setError(null);
      // Le détail de la commande précédente doit partir avec elle : sans ça,
      // son contenu et son PRIX restent affichés sous l'en-tête de la nouvelle
      // le temps de la requête, attribués à la mauvaise commande.
      setDetail(null);
      try {
        const result = await fetchOrderDetail({
          authUser,
          orderId: order.id,
          signal: controller.signal,
        });
        if (active) setDetail(result);
      } catch (err) {
        if (!active || isAbortError(err)) return;
        setError(err);
      } finally {
        if (active) setIsLoading(false);
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [authUser, order.id]);

  const timeline = buildTimeline(order, detail?.events, t);
  const kilos = detail?.kilos ?? order.kilos;
  const bagCount = (detail?.bags || []).reduce(
    (sum, bag) => sum + Number.parseFloat(bag.quantity || 0),
    0
  );

  // Le total découle des lignes affichées. `order.total` sert de repli quand le
  // détail n'a pas encore répondu.
  const grossTotal = detail?.grossTotal || order.total;
  const { net, vat } = splitVat(grossTotal);

  const subtitle = [
    order.room ? `${t("Kamer")} ${order.room}` : null,
    order.department,
    `${t("bestelling")} ${order.reference}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <button type="button" className="mgr-scrim" aria-label={t("Sluiten")} onClick={onClose} />

      <aside className="mgr-drawer" role="dialog" aria-modal="true" aria-label={order.residentName}>
        <div className="mgr-drawer-head">
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 14,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-.4px" }}>
                {order.residentName || order.reference}
              </div>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: "var(--mgr-ink-2)",
                  marginTop: 3,
                }}
              >
                {subtitle}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <button
                type="button"
                className="mgr-icon-btn"
                disabled={!onPrev}
                onClick={onPrev}
                aria-label={t("Vorige")}
              >
                <Icon name="chevron-up" />
              </button>
              <button
                type="button"
                className="mgr-icon-btn"
                disabled={!onNext}
                onClick={onNext}
                aria-label={t("Volgende")}
              >
                <Icon name="chevron-down" />
              </button>
              <button
                type="button"
                className="mgr-icon-btn is-plain"
                onClick={onClose}
                aria-label={t("Sluiten")}
              >
                <Icon name="close" />
              </button>
            </div>
          </div>
          <StatusPill state={order.state} t={t} size="lg" />
        </div>

        <div className="mgr-drawer-body">
          <div>
            <div className="mgr-label">{t("Verloop")}</div>
            {timeline.map((step, index) => (
              <div key={step.key} className="mgr-tl-step">
                <div className="mgr-tl-rail">
                  <span
                    className={`mgr-tl-dot${step.state === "current" ? " is-current" : ""}${
                      step.state === "future" ? " is-future" : ""
                    }`}
                  />
                  {index < timeline.length - 1 ? (
                    <span
                      className={`mgr-tl-line${step.state === "future" ? " is-future" : ""}`}
                    />
                  ) : null}
                </div>
                <div className="mgr-tl-body">
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: step.state === "current" ? 800 : 700,
                      color: step.state === "future" ? "var(--mgr-ink-4)" : "var(--mgr-ink)",
                    }}
                  >
                    {step.label}
                  </div>
                  {step.date ? (
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: step.state === "future" ? "var(--mgr-ink-4)" : "var(--mgr-ink-2)",
                        marginTop: 2,
                      }}
                    >
                      {/* Une date planifiée s'affiche sans heure : `pickup_date`
                          et `delivery_date` n'en portent pas, et en afficher
                          une produisait un « 02:00 » qui n'existe pas. */}
                      {step.precision === "planned"
                        ? `${formatLongDate(step.date)} · ${t("gepland")}`
                        : formatDayTime(step.date)}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>

          {error ? (
            <ErrorState message={t("Het detail kon niet geladen worden.")} />
          ) : isLoading ? (
            <LoadingCard height={120} />
          ) : (
            <>
              <div>
                <div className="mgr-label">{t("Inhoud")}</div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    padding: 16,
                    border: "1px solid var(--mgr-border)",
                    borderRadius: 16,
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 14,
                      background: "var(--mgr-lav-soft)",
                      color: "var(--mgr-brand)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon name="cube-outline" style={{ fontSize: 26 }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>
                      {bagCount > 0 ? `${bagCount} ${t("waszak")}` : t("Geen zakken geregistreerd")}
                      {kilos ? ` · ${formatKilos(kilos)} kg` : ""}
                    </div>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: "var(--mgr-ink-2)",
                        marginTop: 2,
                      }}
                    >
                      {(detail?.bags || []).map((bag) => bag.bag_name).join(", ") ||
                        t("Opgehaald op") + " " + formatShortDate(order.pickupDate)}
                    </div>
                  </div>
                </div>

                {/* Ce que contenait la prestation au kilo : le poids seul ne dit
                    pas ce qui a été lavé, et c'est la question que pose le
                    personnel quand une pièce manque au retour. */}
                {(detail?.kiloPieces || []).length > 0 ? (
                  <div className="mgr-pieces">
                    <div className="mgr-pieces-head">
                      {t("Inhoud van het kilogewicht")}
                      <span className="mgr-pieces-total">
                        {detail.kiloPieces.reduce((sum, piece) => sum + piece.count, 0)}{" "}
                        {t("stuks")}
                      </span>
                    </div>
                    {detail.kiloPieces.map((piece) => (
                      <div key={piece.id} className="mgr-pieces-row">
                        <span>{productName(piece)}</span>
                        <span className="mgr-pieces-count">{piece.count}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>

              <div>
                <div className="mgr-label">{t("Prijs")}</div>
                <div
                  style={{
                    border: "1px solid var(--mgr-border)",
                    borderRadius: 16,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 60px 90px 100px",
                      gap: 8,
                      padding: "11px 16px",
                      background: "var(--mgr-surface-soft)",
                      fontSize: 12.5,
                      fontWeight: 800,
                      letterSpacing: ".5px",
                      textTransform: "uppercase",
                      color: "var(--mgr-ink-2)",
                    }}
                  >
                    <div>{t("Post")}</div>
                    <div className="mgr-num">{t("Aantal")}</div>
                    <div className="mgr-num">{t("Eenheid")}</div>
                    <div className="mgr-num">{t("Totaal")}</div>
                  </div>

                  {(detail?.products || []).length === 0 ? (
                    <div className="mgr-state" style={{ padding: "22px 16px" }}>
                      {t("No items")}
                    </div>
                  ) : (
                    (detail?.products || []).map((product, index) => (
                      <div
                        key={`${product.id || product.name}-${index}`}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 60px 90px 100px",
                          gap: 8,
                          padding: "13px 16px",
                          borderBottom: "1px solid var(--mgr-hairline)",
                          fontSize: 15,
                        }}
                      >
                        <div style={{ fontWeight: 700 }}>{productName(product)}</div>
                        <div className="mgr-num" style={{ fontWeight: 600 }}>
                          {product.quantity}
                        </div>
                        <div className="mgr-num" style={{ fontWeight: 600 }}>
                          {formatCurrency(product.product_price)}
                        </div>
                        <div className="mgr-num" style={{ fontWeight: 800 }}>
                          {formatCurrency(product.product_price * product.quantity)}
                        </div>
                      </div>
                    ))
                  )}

                  <div
                    style={{
                      padding: "12px 16px",
                      background: "var(--mgr-surface-soft)",
                      borderTop: "1px solid var(--mgr-border)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 7,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 15,
                        fontWeight: 600,
                        color: "var(--mgr-ink-2)",
                      }}
                    >
                      <span>{t("Artikelen")}</span>
                      <span>{formatCurrency(detail?.itemsTotal)}</span>
                    </div>
                    {detail?.serviceFee ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: 15,
                          fontWeight: 600,
                          color: "var(--mgr-ink-2)",
                        }}
                      >
                        <span>{t("Service fee")}</span>
                        <span>{formatCurrency(detail.serviceFee)}</span>
                      </div>
                    ) : null}
                    {detail?.promotion ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: 15,
                          fontWeight: 600,
                          color: "var(--mgr-mint-deep)",
                        }}
                      >
                        <span>{t("promotion")}</span>
                        <span>−{formatCurrency(detail.promotion)}</span>
                      </div>
                    ) : null}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 15,
                        fontWeight: 600,
                        color: "var(--mgr-ink-2)",
                      }}
                    >
                      <span>{t("Subtotaal excl. btw")}</span>
                      <span>{formatCurrency(net)}</span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 15,
                        fontWeight: 600,
                        color: "var(--mgr-ink-2)",
                      }}
                    >
                      <span>{t("Btw {{rate}} %", { rate: VAT_RATE })}</span>
                      <span>{formatCurrency(vat)}</span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 19,
                        fontWeight: 800,
                        paddingTop: 7,
                        borderTop: "1px solid var(--mgr-border-mid)",
                      }}
                    >
                      <span>{t("Totaal")}</span>
                      <span>{formatCurrency(grossTotal)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="mgr-drawer-foot">
          <button type="button" className="mgr-cta" onClick={onDownloadReceipt}>
            <Icon name="download-outline" style={{ fontSize: 20 }} />
            {t("Bon downloaden (PDF)")}
          </button>
        </div>
      </aside>
    </>
  );
};

const mapStateToProps = (state) => ({
  authUser: state?.auth?.user,
});

export default compose(withTranslation(), connect(mapStateToProps))(OrderDrawer);
